import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import * as SharedActions from '@states/shared/shared.actions';
import { catchError, map, mergeMap, share, switchMap } from 'rxjs';
import { CustomObjectsActions } from '@states/custom-objects/custom-objects.action-types';
import { CustomObjectsService } from 'src/app/development/custom-objects.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModel } from '@ui';
import { getSavedCustomObjectStates } from '@states/custom-objects/custom-objects.actions';
import { CustomObjectStatesActions } from '@states/custom-objects-states/custom-objects-states.action-types';
import * as CustomObjectStatesActionsImport from '@states/custom-objects-states/custom-objects-states.actions';

@Injectable()
export class CustomObjectsEffect {

  public getSavedCustomObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.getSavedCustomObjects),
      mergeMap(() => {
        return this.customObjectsService.getSavedCustomObjects()
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.getSavedCustomObjectsSuccess({ customObjects: res }), SharedActions.setIsLoading({ isLoading: false })];
            }),
            catchError(error => {
              return [SharedActions.setIsLoading({ isLoading: false }), CustomObjectsActions.getSavedCustomObjectsError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public getSavedCustomObjectStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.getSavedCustomObjectStates),
      mergeMap(() => {
        return this.customObjectsService.getSavedCustomObjectsWithStates()
          .pipe(
            switchMap(res => {
              return [
                CustomObjectsActions.getSavedCustomObjectsSuccess({ customObjects: res.customObjects }),
                CustomObjectStatesActions.getSavedCustomObjectStatesSuccess({ customObjectStates: res.customObjectStates }),
                SharedActions.setIsLoading({ isLoading: false }),
              ];
            }),
            catchError(error => {
              return [
                SharedActions.setIsLoading({ isLoading: false }),
                CustomObjectsActions.getSavedCustomObjectsError({ error }),
                CustomObjectStatesActions.getSavedCustomObjectStatesError({ error }),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getObjectTypeFromPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.getObjectTypeFromPrompt),
      mergeMap(({ prompt }) => {
        return this.customObjectsService.getObjectTypeFromPrompt(prompt)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.getObjectTypeFromPromptSuccess({ objectTypeResponse: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.getObjectTypeFromPromptError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public startTrainingSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.startTrainingSession),
      mergeMap(({ request }) => {
        return this.customObjectsService.startTrainingSession(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.startTrainingSessionSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.startTrainingSessionError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public trainingPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.trainingPhase),
      mergeMap(({ request }) => {
        return this.customObjectsService.trainingPhase(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.trainingPhaseSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.trainingPhaseError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public closeTrainingSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.closeTrainingSession),
      mergeMap(({ request }) => {
        return this.customObjectsService.closeTrainingSession(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.closeTrainingSessionSuccess({ customObject: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.closeTrainingSessionError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public deleteCustomObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.deleteCustomObject),
      mergeMap(({ customObjectId }) => {
        return this.customObjectsService.deleteCustomObject(customObjectId)
          .pipe(
            switchMap(() => {
              return [CustomObjectsActions.deleteCustomObjectSuccess({ customObjectId })];
            }),
            catchError(error => {
              const conflictActions = this.buildAssociatedEventsConflictActions(error, 'DELETE_CUSTOM_OBJECT_MODAL');
              if (conflictActions) {
                return conflictActions;
              }
              return [CustomObjectsActions.deleteCustomObjectError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public updateCustomObjectName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.updateCustomObjectName),
      mergeMap(({ customObjectId, name }) => {
        return this.customObjectsService.updateCustomObjectName(customObjectId, name)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.updateCustomObjectNameSuccess({ customObject: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.updateCustomObjectNameError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public getRepresentatives$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.getRepresentatives),
      mergeMap(({ customObjectId }) => {
        return this.customObjectsService.getRepresentatives(customObjectId)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.getRepresentativesSuccess({ representatives: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.getRepresentativesError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  // Image Upload Flow Effects

  public generateUploadUrls$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.generateUploadUrls),
      mergeMap(({ request }) => {
        return this.customObjectsService.generateUploadUrls(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.generateUploadUrlsSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.generateUploadUrlsError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public evaluateUploadedImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.evaluateUploadedImages),
      mergeMap(({ request }) => {
        return this.customObjectsService.evaluateUploadedImages(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.evaluateUploadedImagesSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.evaluateUploadedImagesError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public imageUploadVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.imageUploadVerification),
      mergeMap(({ request }) => {
        return this.customObjectsService.imageUploadVerification(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.imageUploadVerificationSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.imageUploadVerificationError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  // Custom Classifier (Object States) Effects

  public startClassifierSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.startClassifierSession),
      mergeMap(({ request }) => {
        return this.customObjectsService.startClassifierSession(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.startClassifierSessionSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.startClassifierSessionError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public classifierTrainingPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.classifierTrainingPhase),
      mergeMap(({ request }) => {
        return this.customObjectsService.classifierTrainingPhase(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.classifierTrainingPhaseSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.classifierTrainingPhaseError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public closeClassifierSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectsActions.closeClassifierSession),
      mergeMap(({ request }) => {
        return this.customObjectsService.closeClassifierSession(request)
          .pipe(
            switchMap(res => {
              return [CustomObjectsActions.closeClassifierSessionSuccess({ response: res })];
            }),
            catchError(error => {
              return [CustomObjectsActions.closeClassifierSessionError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  // Custom Object States Effects

  public deleteCustomObjectState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectStatesActionsImport.deleteCustomObjectState),
      mergeMap(({ stateId }) => {
        return this.customObjectsService.deleteCustomObjectState(stateId)
          .pipe(
            switchMap(() => {
              return [CustomObjectStatesActionsImport.deleteCustomObjectStateSuccess({ stateId })];
            }),
            catchError(error => {
              const conflictActions = this.buildAssociatedEventsConflictActions(
                error,
                'DELETE_CUSTOM_OBJECT_STATE_MODAL',
              );
              if (conflictActions) {
                return conflictActions;
              }
              return [CustomObjectStatesActionsImport.deleteCustomObjectStateError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  public updateCustomObjectStateName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CustomObjectStatesActionsImport.updateCustomObjectStateName),
      mergeMap(({ stateId, name }) => {
        return this.customObjectsService.updateCustomObjectStateName(stateId, name)
          .pipe(
            switchMap(res => {
              return [CustomObjectStatesActionsImport.updateCustomObjectStateNameSuccess({ stateCustomObject: res })];
            }),
            catchError(error => {
              return [CustomObjectStatesActionsImport.updateCustomObjectStateNameError({ error })];
            }),
          );
      }),
      share(),
    ),
  );

  private catchError(response: any) {
    console.error('Custom Objects Effect Error:', response);
    return SharedActions.showMessage({ error: response?.error?.message || 'An error occurred with custom objects' });
  }

  // Parse a 409 conflict error carrying an events list ("<message> ## [{ id, name }]")
  // and build an info modal with clickable links to the alerts still using the object.
  // i18nPrefix selects the modal copy (custom object vs. custom object state).
  private buildAssociatedEventsConflictActions(error: any, i18nPrefix: string): Action[] | null {
    if (error?.status !== 409 || !error?.error?.message) {
      return null;
    }

    const message = error.error.message;
    const separator = '##';
    const separatorIndex = message.indexOf(separator);
    if (separatorIndex === -1) {
      return null;
    }

    try {
      // Slice from the first separator so event names containing '##' don't truncate the JSON.
      const jsonPart = message.slice(separatorIndex + separator.length).trim();
      const events = JSON.parse(jsonPart);

      if (!Array.isArray(events) || events.length === 0) {
        return null;
      }

      const eventsList = events
        .map(
          (event: { id: string; name: string }) =>
            `<li><a href="/alert/edit/${event.id}" target="_blank">${event.name}</a></li>`,
        )
        .join('');
      const eventsHtml = `<div style="margin-top: 12px;"><strong>${this.translateService.instant(
        `${i18nPrefix}.REMOVE_FROM_EVENTS_INSTRUCTION`,
      )}</strong><ul style="margin: 8px 0; padding-left: 20px;">${eventsList}</ul></div>`;

      return [
        SharedActions.showConfirmModal({
          options: {
            type: ConfirmDialogModel.ConfirmDialogType.INFO,
            msg: `${this.translateService.instant(`${i18nPrefix}.ASSOCIATED_EVENTS_MSG`, {
              count: events.length,
            })}<br>${eventsHtml}`,
            title: this.translateService.instant(`${i18nPrefix}.TITLE`),
            disableClose: true,
          },
        }),
      ];
    } catch (parseError) {
      console.error('Failed to parse events from error message:', parseError);
      return null;
    }
  }

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private customObjectsService: CustomObjectsService,
    private translateService: TranslateService,
  ) {}
}
